<script setup>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useForm } from "vee-validate";
import * as yup from "yup";
import axios from "@/axios";
import { useAuthStore } from "@/stores/auth";
import Swal from "sweetalert2";
import AuthLayout from "@/components/layouts/AuthLayout.vue";

const router = useRouter();
const authStore = useAuthStore();
const countries = ref([]);
const isLoading = ref(false);
const fetchLoading = ref(true);
const error = ref(null);
const selectedCountry = ref("");

// Esquema de validación
const validationSchema = yup.object({
  country_id: yup.number().required("Por favor selecciona un país"),
});

// Inicializar formulario
const { handleSubmit } = useForm({
  validationSchema,
});

// Obtener lista de países
onMounted(async () => {
  try {
    fetchLoading.value = true;
    const response = await axios.get("/v1/countries");
    countries.value = response.data.countries;
  } catch (err) {
    console.error("Error al cargar países:", err);
    error.value =
      "No pudimos cargar la lista de países. Por favor, intenta nuevamente.";
  } finally {
    fetchLoading.value = false;
  }
});

// Manejar selección de país
const onSubmit = handleSubmit(async () => {
  if (!selectedCountry.value) {
    Swal.fire({
      icon: "warning",
      title: "Selección requerida",
      text: "Por favor selecciona un país para continuar",
    });
    return;
  }

  try {
    isLoading.value = true;

    // Actualizar país del usuario
    const response = await axios.post("/api/v1/update-country", {
      country_id: parseInt(selectedCountry.value),
    });

    // Actualizar datos del usuario en el store
    authStore.updateUser(response.data.user);

    // Mostrar mensaje de éxito
    Swal.fire({
      icon: "success",
      title: "¡Configuración completada!",
      text: "Tu país ha sido registrado correctamente",
      timer: 2000,
      showConfirmButton: false,
    });

    // Redireccionar al dashboard
    router.push("/dashboard");
  } catch (err) {
    console.error("Error al actualizar país:", err);

    let errorMessage = "Ha ocurrido un error al actualizar tu país";
    if (err.response && err.response.data && err.response.data.message) {
      errorMessage = err.response.data.message;
    }

    Swal.fire({
      icon: "error",
      title: "Error",
      text: errorMessage,
    });
  } finally {
    isLoading.value = false;
  }
});

// Manejar cambio de país
const handleCountryChange = (e) => {
  selectedCountry.value = e.target.value;
};
</script>

<template>
  <AuthLayout>
    <template #title> Selecciona tu país </template>

    <div v-if="fetchLoading" class="flex justify-center py-8">
      <div class="spinner flex items-center justify-center">
        <i class="ri-loader-4-line text-white text-3xl animate-spin"></i>
      </div>
    </div>

    <div v-else-if="error" class="text-center py-8">
      <div class="text-red-300 mb-4">
        <i class="ri-error-warning-line text-4xl"></i>
      </div>
      <p class="text-white text-lg">{{ error }}</p>
      <button
        @click="window.location.reload()"
        class="mt-4 px-4 py-2 bg-white text-[#4a5c28] rounded-lg font-medium hover:bg-gray-50 transition-colors"
      >
        Intentar nuevamente
      </button>
    </div>

    <form v-else @submit.prevent="onSubmit" class="space-y-6">
      <div class="text-center mb-6">
        <p class="text-white text-md leading-relaxed">
          Para completar tu registro, selecciona el país donde vives.<br />
          <span class="opacity-80 text-sm"
            >Esta información nos ayuda a personalizar tu experiencia.</span
          >
        </p>
      </div>

      <div class="relative">
        <div
          class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
        >
          <i class="ri-map-pin-line text-gray-400"></i>
        </div>
        <select
          v-model="selectedCountry"
          @change="handleCountryChange"
          class="w-full pl-10 py-4 bg-white/95 rounded-lg shadow-md border border-gray-300 focus:border-[#99b642] focus:ring-2 focus:ring-[#99b642] focus:ring-opacity-50 transition-colors appearance-none text-gray-700"
        >
          <option value="" disabled selected>Selecciona tu país</option>
          <option
            v-for="country in countries"
            :key="country.id"
            :value="country.id"
          >
            {{ country.name }}
          </option>
        </select>
        <div
          class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none"
        >
          <i class="ri-arrow-down-s-line text-gray-400"></i>
        </div>
      </div>

      <button
        type="submit"
        :disabled="isLoading || !selectedCountry"
        class="w-full py-4 px-4 mt-6 bg-[#99b642] hover:bg-[#8ba73b] text-white font-medium rounded-lg shadow-lg focus:outline-none focus:ring-2 focus:ring-[#99b642] focus:ring-opacity-50 transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-70 disabled:hover:bg-[#99b642] disabled:hover:scale-100 disabled:cursor-not-allowed"
      >
        <span v-if="isLoading" class="flex items-center justify-center">
          <i class="ri-loader-4-line animate-spin mr-2"></i>
          Procesando...
        </span>
        <span v-else class="flex items-center justify-center">
          <i class="ri-check-line mr-2"></i>
          Continuar
        </span>
      </button>
    </form>

    <template #footer-links>
      <div class="flex justify-center space-x-2 mt-8">
        <span
          v-for="i in 3"
          :key="i"
          class="h-2.5 w-2.5 rounded-full"
          :class="i === 2 ? 'bg-white' : 'bg-white/40'"
        ></span>
      </div>
      <p class="text-center text-white/80 text-sm mt-3">Paso 2 de 3</p>
    </template>
  </AuthLayout>
</template>
